(ns app.front.view.group.admin.case-id-config
  (:require [app.domain.group.model :as group-model]
            [app.domain.group.number-format-utils :as nf-utils]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.front.entities :as entities]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.config.number-format-editor :as nf-editor]
            [app.front.view.group.admin.subs :as gas]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


(rf/reg-sub
  ::licensing-number-config?
  :<- [::gas/has-permit-type? #{:permit-type/L}]
  :-> identity)

(rf/reg-sub ::has-renewal-number-config?
  :<- [::gas/has-permit-type? ptc-model/permit-types-with-renewal]
  :-> identity)

(rf/reg-event-fx
  ::set-municipal-case-id-config
  (fn [_ [_ group-id new-config]]
    {:comm/command {:command    :group/set-municipal-case-id-config
                    :data       (merge {:group-id group-id}
                                       new-config)
                    :on-success [::config-set-successfully new-config]}}))

(rf/reg-event-fx
  ::check-group-counter-coercion
  (fn [_ [_ new-config resp]]
    (let [intended-value (:group/initial-case-id-counter new-config)
          actual-value   (some->> resp vals (keep :group/initial-case-id-counter) first)]
      ;; Simpler than ::check-group-config-counter-coercion below because saving is handled differently
      (when (> actual-value intended-value)
        {:dispatch [:notifications/add :toast :warning :warning/warning :group-admin/municipal-case-id-coerced]}))))

(rf/reg-event-fx
  ::check-group-config-counter-coercion
  (fn [_ [_ original-config *group-config resp]]
    ;; Intended value is what the user tried to save into the db
    ;; Actual value is what was actually saved to the db (plus one because front shows it as one higher than db)
    ;; and is nil in the resp if it was not changed (coerced when the original value was already minimum)
    (let [intended-value (:group.config/initial-case-id-counter @*group-config)
          actual-value   (or (some->> resp vals (keep :group.config/initial-case-id-counter) first second)
                             (:group.config/initial-case-id-counter original-config))]
      (when (and (number? intended-value)
                 (number? actual-value))
        (when (not= intended-value actual-value)
          (swap! *group-config assoc :group.config/initial-case-id-counter actual-value))
        (when (> actual-value intended-value)
          {:dispatch [:notifications/add :toast :warning :warning/warning :group-admin/municipal-case-id-coerced]})))))

(rf/reg-event-fx
  ::config-set-successfully
  (fn [_ [_ new-config status response]]
    {:dispatch-n [[:notifications/add :toast :success :success ""]
                  [::entities/override-entity-db status response]
                  [::check-group-counter-coercion new-config response]]}))

(rf/reg-sub ::case-id-source
  :<- [::gas/group]
  :-> :group/municipal-case-id-source)


(defn case-id-editor-config-tree
  "The editor for the case id (permit number) template that is used for workspaces that the group-config applies to."
  [{:keys [tr *group-config target parent-config]}]
  (let [authority-group (<sub [::gas/group])
        group-config    (<sub [::entities/entity target])
        group-template  (or (:group/municipal-case-id-template authority-group)
                            nf-utils/default-template-str)
        licensing?      (<sub [::licensing-number-config?])
        get-rollover-v  (fn [attr]
                          (or (attr @*group-config)
                              (attr group-config)
                              (attr parent-config)))]
    ;; Can only edit group-config values if the group has a custom template with a prefix
    ;; (so the group-config templates can have different prefixes)
    (when (and (= (<sub [::case-id-source]) :case-id-source/custom-id)
               (str/includes? group-template "P"))
      [:div.my-4
       (cc/form-group
         {:legend-text (tr [:group.config/custom-permit-number])}
         [nf-editor/number-format-editor
          (merge
            {:tr             tr
             :entity         group-config
             :on-edit        (fn [attr value]
                               (swap! *group-config assoc attr value))
             :rollover-month (get-rollover-v :group.config/municipal-case-id-rollover-month)
             :rollover-day   (get-rollover-v :group.config/municipal-case-id-rollover-day)
             :prefix-attr    :group.config/municipal-case-id-prefix
             :separator-attr :group.config/municipal-case-id-separator
             :counter-attr   :group.config/initial-case-id-counter
             :common-attr    :group.config/municipal-case-id-common-counter?
             :preview-tr-key :group-admin/municipal-case-id-sample-lbl
             :info-tr-keys   [:group-admin/municipal-case-id-info]
             :info-attrs     group-model/inherit-municipal-case-id-attrs
             :source-attr    :group/municipal-case-id-source}
            (if licensing?
              {:rollover-attr  :group.config/municipal-case-id-rollover
               :template-attr  :group.config/municipal-case-id-template
               :digits-attr    :group.config/municipal-case-id-counter-digits
               :renewal-attr   :group.config/municipal-case-id-new-number-on-renewal?
               :disabled-attr? #{:group/municipal-case-id-source}}
              {:rollover-attr  :group/municipal-case-id-rollover
               :template-attr  :group/municipal-case-id-template
               :digits-attr    :group/municipal-case-id-counter-digits
               :disabled-attr? #{:group/municipal-case-id-rollover
                                 :group/municipal-case-id-source
                                 :group/municipal-case-id-template
                                 :group/municipal-case-id-counter-digits}}))])])))


;; This is almost a copy of `case-id-editor-config-tree`, but with enough differences that
;; it kind of makes sense to feature flag from caller side instead of checking flags here
;; in several places.

(defn case-id-editor-config-tree-with-source-selector
  "The editor for the case id (permit number) template that is used for workspaces that the group-config applies to."
  [{:keys [tr *group-config target parent-config]}]
  (let [authority-group        (<sub [::gas/group])
        group-config           (<sub [::entities/entity target])
        licensing?             (<sub [::licensing-number-config?])
        get-rollover-v         (fn [attr]
                                 (or (attr @*group-config)
                                     (attr group-config)
                                     (attr parent-config)))
        manual-case-id-option? (group-model/has-feature? authority-group
                                                         :group.feature/manual-case-ids)]
    ;; Can only edit group-config values if the group has a custom template with a prefix
    ;; (so the group-config templates can have different prefixes)
    [:div.my-4
     (cc/form-group
       {:legend-text (tr [:group.config/custom-permit-number])}
       [nf-editor/number-format-editor-tree
        (merge
          {:tr                tr
           :entity            group-config
           :on-edit           (fn [attr value]
                                (swap! *group-config assoc attr value))
           :rollover-month    (get-rollover-v :group.config/municipal-case-id-rollover-month)
           :rollover-day      (get-rollover-v :group.config/municipal-case-id-rollover-day)
           :prefix-attr       :group.config/municipal-case-id-prefix
           :separator-attr    :group.config/municipal-case-id-separator
           :counter-attr      :group.config/initial-case-id-counter
           :common-attr       :group.config/municipal-case-id-common-counter?
           :preview-tr-key    :group-admin/municipal-case-id-sample-lbl
           :info-tr-keys      [:group-admin/municipal-case-id-info]
           :info-attrs-custom (->> group-model/inherit-municipal-case-id-attrs
                                   (remove #{:group.config/municipal-case-id-source})
                                   set)
           :info-attrs-manual #{:group.config/municipal-case-id-new-number-on-renewal?}
           :source-attr       :group.config/municipal-case-id-source
           :source-on-edit    (fn [value]
                                (swap! *group-config assoc :group.config/municipal-case-id-source value))}
          (when (and
                  (<sub [::ff/enabled? :feature-flag.id/municipal-case-id-year-only-on-renewal?])
                  (<sub [::has-renewal-number-config?]))
            {:year-only-renewal-attr :group/municipal-case-id-year-only-on-renewal?})
          (if licensing?
            {:rollover-attr  :group.config/municipal-case-id-rollover
             :template-attr  :group.config/municipal-case-id-template
             :digits-attr    :group.config/municipal-case-id-counter-digits
             :renewal-attr   :group.config/municipal-case-id-new-number-on-renewal?
             :source-values  (cond-> [:case-id-source/workspace-id
                                      :case-id-source/custom-id]
                               manual-case-id-option? (conj :case-id-source/manual))
             :source-tr-key  :group-admin/municipal-case-id-source-lbl
             :disabled-attr? #{}}
            {:rollover-attr  :group/municipal-case-id-rollover
             :template-attr  :group/municipal-case-id-template
             :digits-attr    :group/municipal-case-id-counter-digits
             :disabled-attr? #{:group/municipal-case-id-rollover
                               :group/municipal-case-id-source
                               :group/municipal-case-id-template
                               :group/municipal-case-id-counter-digits}}))])]))


(defn view [group-id group-has-integration? & [section-options]]
  (let [tr                     (<sub [::gas/group-tr])
        group                  (<sub [::entities/entity group-id])
        licensing?             (<sub [::licensing-number-config?])
        manual-case-id-option? (group-model/has-feature? group :group.feature/manual-case-ids)]
    [acommon/config-section
     (tr [:group-admin/municipal-case-id-title])
     (tr [:group-admin/municipal-case-id-instruction])
     section-options
     (when (:group/municipal-case-id-source group)
       ^{:key (str group-id "-" (:entity/version group) "-case-id-editor")}
       [:form
        [nf-editor/number-format-editor
         {:tr                     tr
          :entity                 group
          :on-save                #(>evt [::set-municipal-case-id-config
                                          (:db/id group)
                                          (->> (merge {:group/initial-case-id-counter     0
                                                       :group/municipal-case-id-separator ""
                                                       :group/municipal-case-id-prefix    ""
                                                       :group/municipal-case-id-template  nf-utils/default-template-str}
                                                      (select-keys group group-model/all-municipal-case-id-attrs)
                                                      %)
                                               (p/filter-vals some?))])
          :year-attr              :group/current-application-id-year
          :template-attr          :group/municipal-case-id-template
          :prefix-attr            :group/municipal-case-id-prefix
          :separator-attr         :group/municipal-case-id-separator
          :rollover-attr          :group/municipal-case-id-rollover
          :counter-attr           :group/initial-case-id-counter
          :digits-attr            :group/municipal-case-id-counter-digits
          :renewal-attr           (when licensing? :group/municipal-case-id-new-number-on-renewal?)
          :year-only-renewal-attr (when (and (<sub [::ff/enabled? :feature-flag.id/municipal-case-id-year-only-on-renewal?])
                                             (<sub [::has-renewal-number-config?]))
                                    :group/municipal-case-id-year-only-on-renewal?)
          :source-attr            :group/municipal-case-id-source
          :source-values          (cond-> [:case-id-source/workspace-id
                                           :case-id-source/custom-id]
                                    manual-case-id-option? (conj :case-id-source/manual)
                                    group-has-integration? (conj :case-id-source/integration))
          :source-tr-key          :group-admin/municipal-case-id-source-lbl
          :info-tr-keys           [:group-admin/municipal-case-id-template-info-v2
                                   :group-admin/municipal-case-id-template-info-v2-common]
          :template-legend-tr-key :group-admin/municipal-case-id-template
          :preview-tr-key         :group-admin/municipal-case-id-sample-lbl
          ;; Disabled group-config attribute included for validation
          :common-attr            :group.config/municipal-case-id-common-counter?
          :disabled-attr?         #{:group.config/municipal-case-id-common-counter?}}]])]))
